import { getErrors } from '@on3/ui-lib/utils/yup';
import {
  forwardRef,
  type HTMLAttributes,
  type ReactNode,
  useEffect,
  useReducer,
} from 'react';
import { type SchemaOf } from 'yup';

enum ActionType {
  UPDATE = 'update',
  SUBMIT = 'submit',
  ERROR = 'error',
  PROCESS = 'process',
}

type Action =
  | { type: ActionType.UPDATE; payload: unknown }
  | { type: ActionType.SUBMIT }
  | { type: ActionType.ERROR; payload: Record<string, string> }
  | { type: ActionType.PROCESS; payload: boolean };

interface State {
  values: unknown;
  errors: Record<string, string>;
  processing: boolean;
  validate: (
    e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>,
  ) => Promise<void>;
}

const initialState: State = {
  values: {},
  errors: {},
  processing: false,
  validate: async () => {},
};

function formReducer(state: State, action: Action): State {
  switch (action.type) {
    case ActionType.UPDATE:
      return { ...state, values: action.payload };
    case ActionType.SUBMIT:
      return { ...state, processing: true, errors: {} };
    case ActionType.ERROR:
      return { ...state, errors: action.payload };
    case ActionType.PROCESS:
      return { ...state, processing: action.payload };
    default:
      return state;
  }
}

interface FormProps extends Omit<HTMLAttributes<HTMLFormElement>, 'children'> {
  values: unknown;
  schema: SchemaOf<unknown>;
  onSubmit: (values: any) => Promise<void> | void;
  children: (state: State, dispatch: React.Dispatch<Action>) => ReactNode;
  className?: string;
}

export const Form = forwardRef<HTMLFormElement, FormProps>(
  (
    { values, schema, onSubmit, children, className, ...props }: FormProps,
    ref,
  ) => {
    const [state, dispatch] = useReducer(formReducer, {
      ...initialState,
      values,
    });

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      dispatch({ type: ActionType.SUBMIT });

      try {
        const validated = await schema.validate(state.values, {
          abortEarly: false,
        });

        await onSubmit(validated);
      } catch (error: any) {
        dispatch({ type: ActionType.ERROR, payload: getErrors(error) });
      } finally {
        dispatch({ type: ActionType.PROCESS, payload: false });
      }
    };

    // Validate single field
    const validate = async (
      e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>,
    ) => {
      const { name } = e.target;

      try {
        await schema.validateAt(name, state.values);
        const { [name]: _, ...rest } = state.errors;

        dispatch({ type: ActionType.ERROR, payload: rest });
      } catch (error: any) {
        dispatch({
          type: ActionType.ERROR,
          payload: { ...state.errors, [name]: error },
        });
      }
    };

    useEffect(() => {
      dispatch({ type: ActionType.UPDATE, payload: values });
    }, [values]);

    return (
      <form className={className} onSubmit={handleSubmit} {...props} ref={ref}>
        {children({ ...state, validate }, dispatch)}
      </form>
    );
  },
);

Form.displayName = 'Form';
